import type { NixiActivity } from '../components/NixiCompanion'

/** JNR-only category saved with a parent-created prompt. */
export type JnrNixiTaskCategory =
  | 'getting_ready'
  | 'food'
  | 'hygiene'
  | 'school'
  | 'play'
  | 'calm'
  | 'bedtime'
  | 'general_support'

export type JnrNixiPresentation = {
  category: JnrNixiTaskCategory
  activity: NixiActivity
  message: string
  isFallback: boolean
}

const scenes: Record<JnrNixiTaskCategory, Omit<JnrNixiPresentation, 'category' | 'isFallback'>> = {
  getting_ready: { activity: 'gettingDressed', message: 'Let’s get ready together!' },
  food: { activity: 'eating', message: 'Yum! Let’s have something to eat.' },
  hygiene: { activity: 'brushingTeeth', message: 'Let’s get clean and fresh.' },
  school: { activity: 'packingBag', message: 'Let’s get ready for preschool!' },
  play: { activity: 'play', message: 'Let’s have some fun!' },
  calm: { activity: 'none', message: 'We can take this nice and slowly.' },
  bedtime: { activity: 'bedtime', message: 'Goodnight. See you tomorrow!' },
  general_support: { activity: 'none', message: 'I’m right here. Let’s do this together!' },
}

function inferCategory(prompt: string): JnrNixiTaskCategory | null {
  const text = prompt.toLocaleLowerCase()
  if (/toilet|bathroom|teeth|brush|wash|shower|hair/.test(text)) return 'hygiene'
  if (/breakfast|snack|lunch|dinner|eat|food|drink/.test(text)) return 'food'
  if (/school|preschool|kinder|bag|pack/.test(text)) return 'school'
  if (/bed|pajama|pjs|sleep|goodnight|teddy/.test(text)) return 'bedtime'
  if (/play|game|park|toy|music|draw|colour/.test(text)) return 'play'
  if (/calm|break|breathe|quiet|rest|sensory/.test(text)) return 'calm'
  if (/dress|shoe|sock|clothes|ready/.test(text)) return 'getting_ready'
  return null
}

/** Guarantees a clean NIXI scene for every JNR parent prompt. */
export function resolveJnrNixiPresentation(
  prompt: string,
  parentCategory?: JnrNixiTaskCategory,
): JnrNixiPresentation {
  const recognised = parentCategory ?? inferCategory(prompt)
  const category = recognised ?? 'general_support'
  return { category, ...scenes[category], isFallback: !recognised }
}

